/**
 * Sales Analytics Service
 * @description API methods for Sales Analytics-related operations
 */

import { api, ApiError } from './api';

// ============================================================================
// Type Definitions
// ============================================================================

export interface KPICard {
  label: string;
  value: number | string;
  value_change: number;
  trend: string;
}

export interface KPICardsResponse {
  domain_id: number;
  domain_name: string;
  period_days: number;
  success_orders: KPICard;
  failed_orders: KPICard;
  stuck_orders: KPICard | null;
  failed_orders_percentage: KPICard;
}

export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface TrendsResponse {
  domain_id: number;
  domain_name: string;
  period: string;
  success_orders: TrendDataPoint[];
  failed_orders: TrendDataPoint[];
  failed_percentage: TrendDataPoint[];
}

export interface StuckOrderDataPoint {
  date: string;
  stuck_orders: number;
  stuck_orders_resolved: number;
}

export interface StuckOrdersResponse {
  data: StuckOrderDataPoint[];
  period: string;
}

export interface TableDataRow {
  id: string | number;
  date: string;
  success_orders: number;
  failed_orders: number;
  stuck_orders: number;
  stuck_orders_resolved: number;
  failure_rate: number | string;
}

export interface TableResponse {
  domain_id: number;
  domain_name: string;
  rows: TableDataRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface Domain {
  id: number;
  domain_name: string;
  is_active: boolean;
}

/** Domain detail for get/update (includes config fields) */
export interface DomainDetail extends Domain {
  failed_order_threshold?: number;
  stuck_order_threshold?: number;
  alert_recipient?: string;
  alerts_enabled?: boolean;
  alert_suppression_hours?: number;
}

export interface UpdateDomainPayload {
  domain_name: string;
  is_active: boolean;
  failed_order_threshold: number;
  stuck_order_threshold: number;
  alert_recipient: string;
  alerts_enabled: boolean;
  alert_suppression_hours: number;
}

export interface PreviewRow {
  row_number: number;
  date: string;
  success_orders: number;
  failed_orders: number;
  stuck_orders: number;
  stuck_orders_resolved: number;
  validation_errors: string[];
}

export interface PreviewResponse {
  domain_id: number | null;
  domain_name: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  preview_rows: PreviewRow[];
  validation_summary: {
    valid_rows: number;
    invalid_rows: number;
    config_errors: string[];
  };
  has_errors: boolean;
}

export interface DomainsResponse {
  sources: Domain[];
  total: number;
}

// ============================================================================
// Service Methods
// ============================================================================

export interface GetKPICardsParams {
  domain_id: number;
  days: number;
}


export type SalesAnalyticsPeriodType = '7d' | '15d' | '30d' | 'all';

export interface GetTrendsParams {
  domain_id: number;
  period: SalesAnalyticsPeriodType;
}

export interface GetStuckOrdersParams {
  domain_id: number;
  period: SalesAnalyticsPeriodType;
}

export interface GetTableParams {
  domain_id: number;
  page?: number;
  page_size?: number;
  date_from?: string; // YYYY-MM-DD format
  date_to?: string; // YYYY-MM-DD format
  period?: SalesAnalyticsPeriodType;
}

export const salesAnalyticsService = {
  /**
   * Fetches KPI cards data for the sales analytics dashboard
   * @param params - Query parameters including domain_id and days
   * @returns KPI cards response with success_orders, failed_orders, stuck_orders, failed_order_percentage
   * @throws ApiError if the request fails
   */
  getKPICards: async (params: GetKPICardsParams): Promise<KPICardsResponse> => {
    try {
      return await api.get<KPICardsResponse>('/sales-analytics/dashboard/kpi-cards', {
        domain_id: params.domain_id,
        days: params.days,
      });
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to fetch KPI cards data');
    }
  },

  /**
   * Fetches trends data for the failed order percentage line chart
   * @param params - Query parameters including domain_id and period
   * @returns Trends response with time-series data
   * @throws ApiError if the request fails
   */
  getTrends: async (params: GetTrendsParams): Promise<TrendsResponse> => {
    try {
      return await api.get<TrendsResponse>('/sales-analytics/dashboard/trends', {
        domain_id: params.domain_id,
        period: params.period,
      });
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to fetch trends data');
    }
  },

  /**
   * Fetches stuck orders data for the bar chart
   * @param params - Query parameters including domain_id and period
   * @returns Stuck orders response with time-series data
   * @throws ApiError if the request fails
   */
  getStuckOrders: async (params: GetStuckOrdersParams): Promise<StuckOrdersResponse> => {
    try {
      return await api.get<StuckOrdersResponse>('/sales-analytics/dashboard/stuck-orders', {
        domain_id: params.domain_id,
        period: params.period,
      });
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to fetch stuck orders data');
    }
  },

  /**
   * Fetches table data for the sales analytics dashboard
   * @param params - Query parameters including domain_id, date range, pagination
   * @returns Table response with data array and pagination metadata
   * @throws ApiError if the request fails
   */
  getTableData: async (params: GetTableParams): Promise<TableResponse> => {
    try {
      return await api.get<TableResponse>('/sales-analytics/dashboard/table', {
        domain_id: params.domain_id,
        page: params.page || 1,
        page_size: params.page_size || 50,
        ...(params.period && { period: params.period }),
        ...(params.date_from && params.date_to && { date_from: params.date_from, date_to: params.date_to }),
      });
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to fetch table data');
    }
  },

  /**
   * Fetches all domains for sales analytics
   * @returns Domains response with array of domain objects
   * @throws ApiError if the request fails (including 404)
   */
  getDomains: async (): Promise<DomainsResponse> => {
    try {
      return await api.get<DomainsResponse>('/sales-analytics/domains');
    } catch (error) {
      // Handle 404 specifically
      if (error instanceof ApiError && error.status === 404) {
        // Return empty response for 404
        return { sources: [], total: 0 };
      }
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to fetch domains');
    }
  },

  /**
   * Fetches a single domain by ID (for edit form)
   */
  getDomain: async (domainId: number): Promise<DomainDetail> => {
    try {
      return await api.get<DomainDetail>(`/sales-analytics/domains/${domainId}`);
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to fetch domain');
    }
  },

  /**
   * Updates a domain's configuration
   */
  updateDomain: async (domainId: number, payload: UpdateDomainPayload): Promise<DomainDetail> => {
    try {
      return await api.put<DomainDetail>(`/sales-analytics/domains/${domainId}`, payload);
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to update domain');
    }
  },

  /**
   * Deletes a domain
   */
  deleteDomain: async (domainId: number): Promise<void> => {
    try {
      await api.delete(`/sales-analytics/domains/${domainId}`);
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to delete domain');
    }
  },

  /**
   * Downloads a sample CSV file for domain configuration
   * @returns Blob of the CSV file
   * @throws ApiError if the request fails
   */
  downloadSampleCSV: async (): Promise<Blob> => {
    try {
      const headers: HeadersInit = {
        'ngrok-skip-browser-warning': 'true',
      };
      
      if (typeof window !== 'undefined') {
        const { env } = await import('@/config/environment');
        const { isTokenExpired } = await import('@/utils/jwtUtils');
        const storedToken = localStorage.getItem(env.JWT_STORAGE_KEY);
        if (storedToken && !isTokenExpired(storedToken)) {
          (headers as Record<string, string>).Authorization = `Bearer ${storedToken}`;
        }
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/sales-analytics/domains/sample-csv`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        let errorData: unknown = undefined;
        try {
          errorData = await response.json();
        } catch {
          try {
            const text = await response.text();
            errorData = { message: text } as { message?: string };
          } catch {
            errorData = {};
          }
        }

        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
        let message = fallbackMessage;
        if (errorData && typeof errorData === 'object') {
          const errorObj = errorData as Record<string, unknown>;
          if ('message' in errorObj && typeof errorObj.message === 'string') {
            message = errorObj.message;
          } else if ('detail' in errorObj && typeof errorObj.detail === 'string') {
            message = errorObj.detail;
          }
        }
        throw new ApiError(message, response.status, errorData);
      }

      return await response.blob();
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to download sample CSV');
    }
  },

  /**
   * Creates a domain with CSV import
   * @param formData - FormData containing domain configuration and CSV file
   * @param mode - Optional mode: 'preview' or 'import' (default: 'import')
   * @returns Created domain response or preview response based on mode
   * @throws ApiError if the request fails
   */
  createDomainWithCSV: async (formData: FormData, mode?: 'preview' | 'execute'): Promise<Domain | PreviewResponse> => {
    try {
      // Add mode to FormData body if provided
      if (mode) {
        formData.append('mode', mode);
      } else {
        formData.append('mode', 'execute');
      }

      const headers: HeadersInit = {
        'ngrok-skip-browser-warning': 'true',
      };
      
      if (typeof window !== 'undefined') {
        const { env } = await import('@/config/environment');
        const { isTokenExpired } = await import('@/utils/jwtUtils');
        const storedToken = localStorage.getItem(env.JWT_STORAGE_KEY);
        if (storedToken && !isTokenExpired(storedToken)) {
          (headers as Record<string, string>).Authorization = `Bearer ${storedToken}`;
        }
      }
      // Don't set Content-Type for FormData - browser will set it with boundary

      const url = `${process.env.NEXT_PUBLIC_API_BASE_URL}/sales-analytics/domains/with-csv`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        let errorData: unknown = undefined;
        try {
          errorData = await response.json();
        } catch {
          try {
            const text = await response.text();
            errorData = { message: text } as { message?: string };
          } catch {
            errorData = {};
          }
        }

        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
        let message = fallbackMessage;
        if (errorData && typeof errorData === 'object') {
          const errorObj = errorData as Record<string, unknown>;
          if ('message' in errorObj && typeof errorObj.message === 'string') {
            message = errorObj.message;
          } else if ('detail' in errorObj && typeof errorObj.detail === 'string') {
            message = errorObj.detail;
          }
        }
        throw new ApiError(message, response.status, errorData);
      }

      const data = await response.json();
      
      // If preview mode, return PreviewResponse, otherwise return Domain
      return data as Domain | PreviewResponse;
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to create domain with CSV');
    }
  },

  /**
   * Imports CSV data to an existing domain (preview or actual import)
   * @param domainId - The domain ID to import data to
   * @param formData - FormData containing CSV file and import parameters
   * @param params - Query parameters (mode, column_mappings, date_format)
   * @returns Import response
   * @throws ApiError if the request fails
   */
  importCSV: async (
    domainId: number,
    formData: FormData,
    params: {
      mode?: 'preview' | 'import';
      column_mappings?: string; // JSON string
      date_format?: string;
    }
  ): Promise<unknown> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('domain_id', String(domainId));
      if (params.mode) queryParams.append('mode', params.mode);
      if (params.column_mappings) queryParams.append('column_mappings', params.column_mappings);
      if (params.date_format) queryParams.append('date_format', params.date_format);
      
      const url = `${process.env.NEXT_PUBLIC_API_BASE_URL}/sales-analytics/import?${queryParams.toString()}`;
      
      const headers: HeadersInit = {
        'ngrok-skip-browser-warning': 'true',
      };
      
      if (typeof window !== 'undefined') {
        const { env } = await import('@/config/environment');
        const { isTokenExpired } = await import('@/utils/jwtUtils');
        const storedToken = localStorage.getItem(env.JWT_STORAGE_KEY);
        if (storedToken && !isTokenExpired(storedToken)) {
          (headers as Record<string, string>).Authorization = `Bearer ${storedToken}`;
        }
      }
      // Don't set Content-Type for FormData - browser will set it with boundary
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        let errorData: unknown = undefined;
        try {
          errorData = await response.json();
        } catch {
          try {
            const text = await response.text();
            errorData = { message: text } as { message?: string };
          } catch {
            errorData = {};
          }
        }

        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
        let message = fallbackMessage;
        if (errorData && typeof errorData === 'object') {
          const errorObj = errorData as Record<string, unknown>;
          if ('message' in errorObj && typeof errorObj.message === 'string') {
            message = errorObj.message;
          } else if ('detail' in errorObj && typeof errorObj.detail === 'string') {
            message = errorObj.detail;
          }
        }
        throw new ApiError(message, response.status, errorData);
      }

      return await response.json();
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to import CSV data');
    }
  },

  /**
   * Adds daily order performance metrics
   * @param domainId - The domain ID
   * @param data - Daily metrics data (date, success_orders, failed_orders, stuck_orders, stuck_orders_resolved)
   * @returns Success response
   * @throws ApiError if the request fails
   */
  addDailyMetrics: async (
    domainId: number,
    data: {
      date: string; // YYYY-MM-DD format
      success_orders: number;
      failed_orders: number;
      stuck_orders: number;
      stuck_orders_resolved: number;
    }
  ): Promise<unknown> => {
    try {
      return await api.post<unknown>(
        `/sales-analytics/metrics/daily`,
        data,
        { domain_id: domainId }
      );
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to add daily metrics');
    }
  },
};

export default salesAnalyticsService;

